import { useEffect } from "react";

const SPEED = 0.4; // pixels per frame
const START_DELAY = 1500; // ms before scrolling starts and after returning to the top
const END_DELAY = 2000; // ms to rest at the bottom
const TOUCH_RESUME_DELAY = 3000; // ms after a touch before scrolling resumes

// Slowly scrolls an overflowing element down, then back to the top, on a loop.
// Pauses while hovered or touched, and stays still if the user prefers reduced motion.
export default function useAutoScroll(element) {
  useEffect(() => {
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame;
    let timeout;
    let resumeTimeout;
    let paused = false;
    // scrollTop rounds small increments away, so track the position separately
    let position = element.scrollTop;

    const step = () => {
      const bottom = element.scrollHeight - element.clientHeight;
      if (bottom <= 0) return; // nothing to scroll

      if (!paused) {
        if (position >= bottom) {
          timeout = setTimeout(() => {
            element.scrollTo({ top: 0, behavior: "smooth" });
            position = 0;
            timeout = setTimeout(() => {
              frame = requestAnimationFrame(step);
            }, START_DELAY);
          }, END_DELAY);
          return;
        }
        position = Math.min(position + SPEED, bottom);
        element.scrollTop = position;
      }
      frame = requestAnimationFrame(step);
    };

    const pause = () => {
      paused = true;
      clearTimeout(resumeTimeout);
    };
    const resume = () => {
      position = element.scrollTop; // continue from wherever the user left it
      paused = false;
    };
    const resumeLater = () => {
      resumeTimeout = setTimeout(resume, TOUCH_RESUME_DELAY);
    };

    element.addEventListener("mouseenter", pause);
    element.addEventListener("mouseleave", resume);
    element.addEventListener("touchstart", pause, { passive: true });
    element.addEventListener("touchend", resumeLater);

    timeout = setTimeout(() => {
      frame = requestAnimationFrame(step);
    }, START_DELAY);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timeout);
      clearTimeout(resumeTimeout);
      element.removeEventListener("mouseenter", pause);
      element.removeEventListener("mouseleave", resume);
      element.removeEventListener("touchstart", pause);
      element.removeEventListener("touchend", resumeLater);
    };
  }, [element]);
}
